import { zafClient, getZendeskUrl, isProduction } from "@service/zendesk";
import { resolveFields } from "@service/zendesk/fieldResolver";

import { type ServiceOrder, type CreateTicketResponse } from "@interfaces/ServiceOrder";
import { getOrCreateUser } from "./user";

const safeStr = (val: string | null | undefined): string | undefined =>
  val && val.trim() ? val.trim() : undefined;

export const createTicket = async (
  body: ServiceOrder,
  description: string,
  mainTicket: number
): Promise<CreateTicketResponse> => {
  let userId: number | null = null;
  const IsCpf = body.consumidor.cpf.replace(/\D/g, "").length === 11;
  const IsCnpj = body.consumidor.cpf.replace(/\D/g, "").length === 14;

  const enderecoOS = safeStr(body.consumidor.endereco);
  const numeroOS = safeStr(body.consumidor.numero);
  const enderecoCompleto = enderecoOS
    ? numeroOS
      ? `${enderecoOS}, ${numeroOS}`
      : enderecoOS
    : undefined;

  const user = await getOrCreateUser(body.consumidor.nome, body.consumidor.email, {
    cpf_3: IsCpf ? body.consumidor.cpf : undefined,
    cnpj_regex: IsCnpj ? body.consumidor.cpf : undefined,
    endereco_2: enderecoCompleto,
    cidade: safeStr(body.consumidor.cidade),
    _pessoa_fisica__bairro_de_cobranca: safeStr(body.consumidor.bairro),
    _pessoa_fisica__bairro_de_entrega: safeStr(body.consumidor.bairro),
    _pessoa_fisica__endereco_de_entrega: enderecoOS,
    estado: safeStr(body.consumidor.estado),
    tipo_de_conta: body.consumidor.tipo_pessoa === "F" ? "pessoa_fisica" : "pessoa_juridica",
    cep__endereco: safeStr(body.consumidor.cep),
    outro_telefone: safeStr(body.consumidor.telefone),
  });
  userId = user.id;

  const f = await resolveFields();

  const response = await zafClient.request({
    url: getZendeskUrl() + "/api/v2/tickets.json",
    type: "POST",
    contentType: "application/json",
    data: JSON.stringify({
      ticket: {
        subject: `OS - ${mainTicket}`,
        comment: {
          body: description,
          public: false,
        },
        brand_id: isProduction() ? 40709538471444 : 45750827384340,
        type: "incident",
        problem_id: mainTicket,
        requester_id: userId,
        priority: "low",
        ticket_form_id: isProduction() ? 38306938336788 : 45751774116884,
        custom_fields: [
          { id: f("[Ordem de Servico] Código OS"), value: body.os.sua_os },
          { id: f("[Ordem de Servico] Número da OS"), value: body.os.sua_os },
          {
            id: f("[Ordem de Servico] Status"),
            value:
              body.os.status === "Finalizada" || body.os.finalizada
                ? "fechado_status_ordem_servico"
                : "",
          },
          {
            id: f("[Ordem de Servico] Dias em aberto"),
            value: Number(body.os.fechado_em.match(/\d+/)?.[0]),
          },
          {
            id: f("[Ordem de Servico] Data de digitação"),
            value: body.os.data_digitacao.split("/").reverse().join("-"),
          },
          { id: f("[Ordem de Servico] Data de início"), value: body.os.data_nota_fiscal },
          { id: f("[Ordem de Servico] OS principal"), value: true },
          {
            id: f("[Ordem de Servico] Tipo de atendimento"),
            value:
              body.os.tipo_atendimento.charAt(0).toLowerCase() + body.os.tipo_atendimento.slice(1),
          },
          {
            id: f("[Ordem de Servico] Data de entrada posto"),
            value: body.os.data_entrada_produto_posto?.split("/").reverse().join("-"),
          },
          { id: f("[Ordem de Servico] Data de início"), value: body.os.finalizada },
          { id: f("[Ordem de Servico] Data do conserto"), value: body.os.data_conserto },
          {
            id: f("[Ordem de Servico] Data da retirada do produto"),
            value: body.os.data_retirada_produto_consumidor?.split("/").reverse().join("-"),
          },
          {
            id: f("[Ordem de Servico] Fechado"),
            value: body.os.status === "Finalizada" ? true : false,
          },
          { id: f("[Ordem de Servico] Número de série"), value: body.produto.serie },
          { id: f("[Ordem de Servico] Aparência"), value: body.produto.aparencia },
          { id: f("[Ordem de Servico] Acessório"), value: body.produto.acessorio },
          {
            id: f("[Ordem de Servico] Informações sobre o defeito"),
            value: body.produto.informacoes_sobre_defeito,
          },
          { id: f("Defeito reclamado"), value: body.defeito.reclamado },
          { id: f("[Ordem de Servico] Defeito constatado"), value: body.defeito.constatado },
          { id: f("[Ordem de Servico] Solução"), value: body.defeito.solucao },
          { id: f("[Ordem de Servico] Data finalizada"), value: body.os.finalizada },
          { id: f("[Ordem de Servico] Responsável"), value: body.troca_ressarcimento.responsavel },
          {
            id: f("[Ordem de Servico] Gera pedido"),
            value: body.troca_ressarcimento.gera_pedido === "não" ? false : true,
          },
          { id: f("[Ordem de Servico] Transporte"), value: body.troca_ressarcimento.transporte },
          {
            id: f("[Ordem de Servico] Situação do atendimento"),
            value: (body.troca_ressarcimento.situacao_atendimento ?? "").includes("Garantia")
              ? "produto_em_garantia"
              : (body.troca_ressarcimento.situacao_atendimento ?? "").includes("50%")
                ? "faturado_50"
                : (body.troca_ressarcimento.situacao_atendimento ?? "").includes("100%")
                  ? "faturado_100"
                  : "desconhecido",
          },
          { id: f("[Ordem de Servico] Motivo"), value: body.troca_ressarcimento.motivo },
          {
            id: f("[Ordem de Servico] Causa troca OS"),
            value: body.troca_ressarcimento.causa_troca,
          },
          {
            id: f("[Ordem de Servico] Observação da troca"),
            value: body.troca_ressarcimento.observacao,
          },
          { id: f("[Ordem de Servico] NF da revenda"), value: body.revenda.nota_fiscal },
          { id: f("[Ordem de Servico] Telefone"), value: body.consumidor.telefone },
          { id: f("[Ordem de Servico] Celular"), value: body.consumidor.celular },
          { id: f("[Ordem de Servico] E-mail"), value: body.consumidor.email },
          { id: f("[Ordem de Servico] Endereço"), value: body.consumidor.endereco },
          { id: f("[Ordem de Servico] Número do endereço"), value: body.consumidor.numero },
          { id: f("[Ordem de Servico] Bairro"), value: body.consumidor.bairro },
          { id: f("[Ordem de Servico] CEP"), value: body.consumidor.cep },
          { id: f("[Geral] Cidade"), value: body.consumidor.cidade },
          { id: f("[SAC ECOMM] Cidade/Estado da OS"), value: body.consumidor.estado },
          { id: f("[Ordem de Servico] CNPJ Revenda"), value: body.revenda.cnpj },
          { id: f("[Ordem de Servico] Código do posto"), value: body.posto.codigo_posto },
          { id: f("[Ordem de Servico] CPF/CNPJ"), value: body.consumidor.cpf },
          { id: f("[Ordem de Servico] Estado"), value: body.consumidor.estado },
          {
            id: f("[Ordem de Servico] Data da troca"),
            value: body.troca_ressarcimento.data?.split("/").reverse().join("-"),
          },
          {
            id: f("[Ordem de Servico] Data da NF"),
            value: body.revenda.data_nota_fiscal?.split("/").reverse().join("-"),
          },
          { id: f("[Ordem de Servico] Fechada em"), value: body.os.fechado_em },
          {
            id: f("[Ordem de Servico] Finalizada"),
            value: body.os.finalizada?.split("/").reverse().join("-"),
          },
          { id: f("[Ordem de Servico] Nome do consumidor"), value: body.consumidor.nome },
          { id: f("[Ordem de Servico] Nome Revenda"), value: body.revenda.nome },
          { id: f("[Ordem de Servico] Observação"), value: body.obs },
          { id: f("[Ordem de Servico] Ordem de serviço pai"), value: body.os.sua_os },
          {
            id: f("[Ordem de Servico] Posui pedido de peça"),
            value: body.peca ? "sim_pedido_peca" : "não_pedido_peca",
          },
          {
            id: f("[Ordem de Servico] Situação atendimento OS"),
            value: body.troca_ressarcimento.situacao_atendimento,
          },
          { id: f("[Ordem de Servico] Orientação SAC"), value: body.orientacao_sac },
          { id: f("[Ordem de Servico] Trocado por"), value: body.troca_ressarcimento.trocado_por },
          {
            id: f("[Ordem de Servico] Setor"),
            value:
              body.troca_ressarcimento.setor === "Revenda"
                ? "revenda_setor"
                : body.troca_ressarcimento.setor === "Carteira"
                  ? "carteira"
                  : body.troca_ressarcimento.setor === "SAC"
                    ? "sac_setor"
                    : body.troca_ressarcimento.setor === "Procon"
                      ? "procon_setor"
                      : body.troca_ressarcimento.setor === "SAP"
                        ? "sap"
                        : body.troca_ressarcimento.setor === "Suporte Técnico"
                          ? "suporte_técnico"
                          : null,
          },
        ],
      },
    }),
  });

  return response as CreateTicketResponse;
};
